import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import Dashboard from "./pages/Dashboard";
import ApplicationForm from "./pages/ApplicationForm";
import MockEmailPage from "./pages/MockEmailPage";
import MockRecommendationEmail from "./pages/MockRecommendationEmail";
import RecommenderPortal from "./pages/RecommenderPortal";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ObservationPortal from "./pages/ObservationPortal";
import MockObservationEmail from "./pages/MockObservationEmail";
import EmploymentVerificationPortal from "./pages/EmploymentVerificationPortal";
import MockEmploymentEmail from "./pages/MockEmploymentEmail";
import { Toaster } from "@/components/ui/sonner";
import SitePasswordGate from "./components/SitePasswordGate";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Theme Context
const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
};

const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem("theme");
    return savedTheme || "dark";
  });

  useEffect(() => {
    localStorage.setItem("theme", theme);
    // Apply theme class to document root and body
    const root = document.documentElement;
    const body = document.body;
    
    if (theme === "light") {
      root.classList.add("light-mode");
      root.classList.remove("dark-mode");
      body.classList.add("light-mode");
      body.classList.remove("dark-mode");
      body.style.backgroundColor = "#F8FAFC";
      body.style.color = "#1E293B";
    } else {
      root.classList.add("dark-mode");
      root.classList.remove("light-mode");
      body.classList.add("dark-mode");
      body.classList.remove("light-mode");
      body.style.backgroundColor = "#0A0E14";
      body.style.color = "#F5F7F9";
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === "dark" ? "light" : "dark");
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          console.error("Auth error:", error);
          localStorage.removeItem("token");
          setToken(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, firstName, lastName, consentPreferences = {}) => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      first_name: firstName,
      last_name: lastName,
      phone: consentPreferences.phone,
      consent_call: consentPreferences.consent_call ?? true,
      consent_text: consentPreferences.consent_text ?? true,
      consent_email: consentPreferences.consent_email ?? true
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const updateUser = (updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    updateUser,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const { theme } = useTheme();

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${theme === 'dark' ? 'bg-[#0A0E14]' : 'bg-gray-50'}`}>
        <div className="w-12 h-12 border-4 border-[#00677F] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}

function AppContent() {
  const { theme } = useTheme();
  
  return (
    <div className={`App min-h-screen ${theme === 'dark' ? 'bg-[#0A0E14]' : 'bg-gray-50'}`}>
      <BrowserRouter>
        <Routes>
          {/* Public routes - no site password gate */}
          <Route path="/recommend/:token" element={<RecommenderPortal />} />
          <Route path="/mock-recommendation-email" element={<MockRecommendationEmail />} />
          <Route path="/observation/:token" element={<ObservationPortal />} />
          <Route path="/mock-observation-email" element={<MockObservationEmail />} />
          <Route path="/employment-verify/:token" element={<EmploymentVerificationPortal />} />
          <Route path="/mock-employment-email" element={<MockEmploymentEmail />} />
          {/* All other routes - behind site password gate */}
          <Route path="/*" element={
            <SitePasswordGate>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/mock-email" element={<MockEmailPage />} />
                <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/application/:appId"
                  element={
                    <ProtectedRoute>
                      <ApplicationForm />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/application/:appId/:step"
                  element={
                    <ProtectedRoute>
                      <ApplicationForm />
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </SitePasswordGate>
          } />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
